import React, { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, Calendar, ArrowRight, Share2 } from 'lucide-react';
import { HomlyLogo } from '../../components/brand/HomlyLogo';
import { BLOG_ARTICLES } from './blogData';

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function BlogArticlePage() {
  const { slug } = useParams();
  const article = BLOG_ARTICLES.find(a => a.slug === slug);
  const currentIndex = BLOG_ARTICLES.findIndex(a => a.slug === slug);
  const nextArticle = BLOG_ARTICLES[(currentIndex + 1) % BLOG_ARTICLES.length];

  useEffect(() => {
    window.scrollTo(0, 0);
    if (article) {
      document.title = `${article.title} — Blog Homly`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute('content', article.metaDescription);
    }
  }, [article, slug]);

  if (!article) return <Navigate to="/blog" replace />;

  const handleShare = async () => {
    if (navigator.share) {
      await navigator.share({ title: article.title, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a1a]">
      {/* Navbar */}
      <nav className="sticky top-0 z-50 bg-[#0a0a1a]/95 backdrop-blur-md border-b border-white/[0.06]" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between">
          <Link to="/blog" className="flex items-center gap-3" data-testid="article-back-blog">
            <HomlyLogo size="md" />
            <span className="text-xs font-medium text-white/30 border-l border-white/10 pl-3">Blog</span>
          </Link>
          <button
            onClick={handleShare}
            data-testid="article-share-btn"
            className="text-white/40 hover:text-white transition-colors p-2 rounded-lg hover:bg-white/5"
          >
            <Share2 className="w-4 h-4" />
          </button>
        </div>
      </nav>

      {/* Article header */}
      <header className="pt-12 sm:pt-16 pb-8 sm:pb-10 px-4">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-white/30 hover:text-white/60 transition-colors mb-6">
              <ArrowLeft className="w-3.5 h-3.5" />Tous les articles
            </Link>

            <div className="flex items-center gap-3 mb-5">
              <span className={`text-xs font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full bg-gradient-to-r ${article.heroColor} text-white`}>
                {article.category}
              </span>
              <span className="text-xs text-white/30 flex items-center gap-1">
                <Clock className="w-3 h-3" />{article.readTime}
              </span>
              <span className="text-xs text-white/30 flex items-center gap-1">
                <Calendar className="w-3 h-3" />{formatDate(article.date)}
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white tracking-tight mb-4 leading-tight">
              {article.title}
            </h1>

            <p className="text-base sm:text-lg text-white/45 leading-relaxed">
              {article.excerpt}
            </p>
          </motion.div>
        </div>
      </header>

      {/* Separator */}
      <div className="max-w-3xl mx-auto px-4">
        <div className={`h-0.5 rounded-full bg-gradient-to-r ${article.heroColor} opacity-20`} />
      </div>

      {/* Article content */}
      <article className="px-4 py-10 sm:py-14" data-testid="article-content">
        <div className="max-w-3xl mx-auto">
          {article.content.map((block, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.05 * i }}
            >
              {block.type === 'heading' ? (
                <h2 className="text-xl sm:text-2xl font-bold text-white mt-10 mb-4 tracking-tight">
                  {block.text}
                </h2>
              ) : (
                <p className="text-[15px] sm:text-base text-white/55 leading-[1.85] mb-5">
                  {block.text}
                </p>
              )}
            </motion.div>
          ))}
        </div>
      </article>

      {/* Author + CTA */}
      <section className="px-4 pb-10">
        <div className="max-w-3xl mx-auto space-y-6">
          {/* Author bar */}
          <div className="flex items-center gap-4 py-5 border-t border-white/[0.06]">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center text-white font-bold text-sm">H</div>
            <div>
              <p className="text-sm font-medium text-white">{article.author}</p>
              <p className="text-xs text-white/30">{formatDate(article.date)}</p>
            </div>
          </div>

          {/* CTA */}
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 sm:p-8 text-center">
            <h3 className="text-lg font-bold text-white mb-2">Envie de mettre ça en pratique ?</h3>
            <p className="text-sm text-white/40 mb-5">Découvrez comment Homly aide les agences à piloter leurs mandats.</p>
            <Link
              to="/agences"
              data-testid="article-cta-demo"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-bold text-sm hover:opacity-90 transition-opacity"
            >
              Demander une démo <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Next article */}
      {nextArticle && nextArticle.slug !== article.slug && (
        <section className="px-4 pb-20">
          <div className="max-w-3xl mx-auto">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-white/20 mb-4">Article suivant</p>
            <Link
              to={`/blog/${nextArticle.slug}`}
              data-testid="article-next"
              className="group block rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 transition-all duration-300 hover:border-white/[0.12] hover:bg-white/[0.04]"
            >
              <div className="flex items-center justify-between">
                <div>
                  <span className={`text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-gradient-to-r ${nextArticle.heroColor} text-white`}>
                    {nextArticle.category}
                  </span>
                  <h4 className="text-base sm:text-lg font-bold text-white mt-2 group-hover:text-white/90 transition-colors">
                    {nextArticle.title}
                  </h4>
                </div>
                <ArrowRight className="w-5 h-5 text-white/30 group-hover:text-white/60 transition-all group-hover:translate-x-1 flex-shrink-0 ml-4" />
              </div>
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
